'use client'

import { memo } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

type GraphicProps = { className?: string }

/** Enterprise hero: sovereign datacenter + shield perimeter */
export const NinxlEnterpriseHeroScene = memo(function NinxlEnterpriseHeroScene({ className }: GraphicProps) {
  const shouldReduce = useReducedMotion()

  return (
    <svg viewBox="0 0 900 420" className={cn('h-full w-full', className)} aria-hidden>
      <defs>
        <linearGradient id="ent-hero-glow" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#21728d" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#88a1ac" stopOpacity="0.06" />
        </linearGradient>
        <linearGradient id="ent-shield" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#21728d" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#134b5f" stopOpacity="0.12" />
        </linearGradient>
      </defs>

      <rect width="900" height="420" fill="url(#ent-hero-glow)" rx="24" />
      <ellipse cx="450" cy="360" rx="360" ry="42" fill="#21728d" fillOpacity="0.06" />

      {/* Perimeter ring */}
      <circle
        cx="450"
        cy="190"
        r="148"
        fill="none"
        stroke="#21728d"
        strokeWidth="1.5"
        strokeOpacity="0.28"
        strokeDasharray="10 8"
        className="ninxl-graphic-orbit"
      />
      <circle
        cx="450"
        cy="190"
        r="118"
        fill="url(#ent-shield)"
        stroke="#21728d"
        strokeWidth="2"
        strokeOpacity="0.35"
      />

      {/* Core node */}
      <rect x="378" y="148" width="144" height="84" rx="18" fill="var(--ninxl-theme-panel, #fff)" stroke="#21728d" strokeWidth="2" strokeOpacity="0.45" />
      <text x="450" y="182" textAnchor="middle" className="fill-[#134b5f] text-[13px] font-bold">
        NINX Enterprise
      </text>
      <text x="450" y="204" textAnchor="middle" className="fill-[#21728d] text-[10px] font-semibold">
        Secure Local Perimeter
      </text>

      {/* Satellite systems */}
      {[
        { x: 170, y: 120, label: 'Email' },
        { x: 730, y: 120, label: 'ERP' },
        { x: 140, y: 260, label: 'CRM' },
        { x: 760, y: 260, label: 'Calendar' },
      ].map((node, index) => (
        <g key={node.label}>
          <line
            x1="450"
            y1="190"
            x2={node.x}
            y2={node.y}
            stroke="#88a1ac"
            strokeWidth="1.2"
            strokeOpacity="0.45"
            strokeDasharray="5 4"
          />
          <rect
            x={node.x - 44}
            y={node.y - 22}
            width="88"
            height="44"
            rx="14"
            fill="var(--ninxl-theme-panel, #fff)"
            stroke="#21728d"
            strokeWidth="1.2"
            strokeOpacity="0.3"
          />
          <text x={node.x} y={node.y + 5} textAnchor="middle" className="fill-[#5f7380] text-[10px] font-semibold">
            {node.label}
          </text>
          {!shouldReduce ? (
            <motion.circle
              r="4"
              fill="#21728d"
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0.2, 1, 0.2],
                cx: [450, node.x],
                cy: [190, node.y],
              }}
              transition={{
                duration: 2.4,
                repeat: Infinity,
                delay: index * 0.45,
                ease: 'easeInOut',
              }}
            />
          ) : null}
        </g>
      ))}

      {/* Privacy gate */}
      <rect x="392" y="96" width="116" height="28" rx="14" fill="#21728d" fillOpacity="0.12" stroke="#21728d" strokeWidth="1.2" />
      <text x="450" y="115" textAnchor="middle" className="fill-[#134b5f] text-[9px] font-bold tracking-[0.12em]">
        PRIVACY GATE
      </text>

      {/* Optional cloud: dashed, opt-in */}
      <rect x="700" y="48" width="120" height="40" rx="12" fill="none" stroke="#88a1ac" strokeWidth="1.2" strokeDasharray="4 4" strokeOpacity="0.5" />
      <text x="760" y="73" textAnchor="middle" className="fill-[#88a1ac] text-[9px] font-medium">
        Approved Cloud
      </text>
      <path d="M618 148 C660 110 700 90 760 68" fill="none" stroke="#88a1ac" strokeWidth="1" strokeDasharray="6 5" strokeOpacity="0.35" />
    </svg>
  )
})

/** Privacy router diagram */
export const NinxlEnterpriseRouterDiagram = memo(function NinxlEnterpriseRouterDiagram({ className }: GraphicProps) {
  return (
    <div className={cn('ninxl-enterprise-router rounded-3xl border border-[#21728d]/20 p-6 shadow-lg shadow-[#21728d]/8', className)}>
      <svg viewBox="0 0 640 220" className="h-auto w-full" aria-hidden>
        <rect x="24" y="78" width="140" height="64" rx="16" fill="#21728d" fillOpacity="0.08" stroke="#21728d" strokeWidth="1.4" />
        <text x="94" y="106" textAnchor="middle" className="fill-[#134b5f] text-[11px] font-bold">Incoming Task</text>
        <text x="94" y="124" textAnchor="middle" className="fill-[#5f7380] text-[9px]">Sensitivity scan</text>

        <rect x="250" y="64" width="140" height="92" rx="18" fill="#134b5f" fillOpacity="0.92" stroke="#21728d" strokeWidth="2" />
        <text x="320" y="104" textAnchor="middle" className="fill-white text-[11px] font-bold">Privacy Gate</text>
        <text x="320" y="124" textAnchor="middle" className="fill-[#c5e8f2] text-[9px]">Hybrid Router</text>

        <rect x="476" y="48" width="140" height="56" rx="14" fill="#21728d" fillOpacity="0.14" stroke="#21728d" strokeWidth="1.4" />
        <text x="546" y="74" textAnchor="middle" className="fill-[#134b5f] text-[10px] font-bold">Local Engine</text>
        <text x="546" y="90" textAnchor="middle" className="fill-[#21728d] text-[8px]">Zero-latency</text>

        <rect x="476" y="128" width="140" height="56" rx="14" fill="none" stroke="#88a1ac" strokeWidth="1.2" strokeDasharray="5 4" />
        <text x="546" y="154" textAnchor="middle" className="fill-[#5f7380] text-[10px] font-bold">Approved Cloud</text>
        <text x="546" y="170" textAnchor="middle" className="fill-[#88a1ac] text-[8px]">Opt-in only</text>

        <path d="M164 110 H250" stroke="#21728d" strokeWidth="2" markerEnd="url(#ent-arrow)" />
        <path d="M390 96 H476" stroke="#21728d" strokeWidth="2" />
        <path d="M390 124 H476" stroke="#88a1ac" strokeWidth="1.5" strokeDasharray="5 4" />

        <defs>
          <marker id="ent-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill="#21728d" />
          </marker>
        </defs>
      </svg>
    </div>
  )
})

/** Workflow pipeline graphic */
export const NinxlEnterpriseWorkflowPipeline = memo(function NinxlEnterpriseWorkflowPipeline({
  steps,
  className,
}: {
  steps: readonly { step: string; detail: string }[]
  className?: string
}) {
  const shouldReduce = useReducedMotion()

  return (
    <div className={cn('relative', className)}>
      <div className="absolute left-8 right-8 top-1/2 hidden h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-[#21728d]/35 to-transparent md:block" aria-hidden />
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        {steps.map((item, index) => (
          <motion.div
            key={item.step}
            className="ninxl-enterprise-step relative rounded-2xl border border-[#21728d]/15 p-4 shadow-sm"
            initial={shouldReduce ? false : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-24px' }}
            transition={{ duration: 0.5, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-[#21728d]/10 text-xs font-bold text-[#134b5f]">
              {index + 1}
            </div>
            <div className="ninxl-theme-text mb-1 text-sm font-semibold">{item.step}</div>
            <p className="ninxl-theme-text-muted text-xs leading-relaxed">{item.detail}</p>
          </motion.div>
        ))}
      </div>
    </div>
  )
})

export const NinxlEnterpriseSectionBackdrop = memo(function NinxlEnterpriseSectionBackdrop({
  variant,
  className,
}: {
  variant: 'paradox' | 'hybrid' | 'hardware' | 'governance' | 'industries'
  className?: string
}) {
  const washes: Record<typeof variant, string> = {
    paradox: 'from-rose-500/[0.04] via-transparent to-transparent',
    hybrid: 'from-[#21728d]/[0.07] via-transparent to-sky-500/[0.03]',
    hardware: 'from-amber-500/[0.05] via-transparent to-[#21728d]/[0.04]',
    governance: 'from-violet-500/[0.04] via-transparent to-[#21728d]/[0.03]',
    industries: 'from-emerald-500/[0.04] via-transparent to-[#21728d]/[0.03]',
  }

  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b',
        washes[variant],
        className,
      )}
      aria-hidden
    />
  )
})